import { Component, OnInit, Input } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { FoodService } from 'app/services';
import { FoodProductStatus } from 'app/models';

enum EditProductTab {
    ProductInfo = 1,
    ModifierGroups = 2,
    Categories
}

@Component({
    selector: 'edit-food-product',
    templateUrl: './edit-food-product.component.html',
    styleUrls: ['./edit-food-product.component.scss'],
    providers: [MessageService]
})
export class EditFoodProductComponent implements OnInit {
    @Input()
    public productId: number = 0;

    @Input()
    public restaurants: Array<any> = [];

    @Input()
    public onSave: () => void = () => { };

    @Input()
    public onHide: () => void = () => { };

    public editProductTab = EditProductTab;
    public activeTab: EditProductTab = EditProductTab.ProductInfo;

    public menuItems: MenuItem[] = [
        {
            label: 'Product',
            id: `${EditProductTab.ProductInfo}`,
            command: (e: any) => this.handleTabChange(+e.item.id)
        },
        {
            label: 'Modifier Groups',
            id: `${EditProductTab.ModifierGroups}`,
            command: (e: any) => this.handleTabChange(+e.item.id)
        },
        {
            label: 'Categories',
            id: `${EditProductTab.Categories}`,
            command: (e: any) => this.handleTabChange(+e.item.id)
        }
    ];

    public product: any = {};

    public isEnabled: boolean = true;

    public allModifierGroups: Array<any> = [];
    public freeModifierGroups: Array<any> = [];
    public productModifierGroups: Array<any> = [];
    public productModifierGroupsChanged: boolean = false;

    public modifierGroupCols: any[] = [
        { field: 'Id', header: 'Id', width: '90px' },
        { field: 'Name', header: 'Name', width: '200px' }
    ];

    public allCategories: Array<any> = [];
    public freeCategories: Array<any> = [];
    public productCategories: Array<any> = [];
    public productCategoriesChanged: boolean = false;

    public categoriesCols: any[] = [
        { field: 'Id', header: 'Id', width: '90px' },
        { field: 'Name', header: 'Name', width: '200px' }
    ];

    constructor(
        private messageService: MessageService,
        private foodService: FoodService) {
    }

    ngOnInit(): void {
        if (this.productId) {
            this.getProduct();
        }
    }

    private getProduct(): void {
        const dto = {
            Id: this.productId
        };

        this.foodService.getProductById(dto, this.messageService)
            .then(data => {
                if (!data) {
                    return;
                }

                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }

                this.product = data;

                this.isEnabled = this.product.StatusId === FoodProductStatus.Enabled;

                this.getProductModifierGroups();
                this.getProductCategories();
            });
    }

    private getAllModifierGroups(): void {
        if (!this.product.RestaurantId) {
            return;
        }

        const dto = {
            RestaurantId: this.product.RestaurantId
        };

        this.foodService.getModifierGroups(dto, this.messageService)
            .then(data => {
                if (!data) {
                    return;
                }

                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }

                this.allModifierGroups = data.GroupList;

                this.filterFreeModifierGroups();
            });
    }

    private getProductModifierGroups(): void {
        const dto = {
            RestaurantId: this.product.RestaurantId,
            ProductId: this.product.Id
        };

        this.foodService.getModifierGroups(dto, this.messageService)
            .then(data => {
                if (!data) {
                    return;
                }

                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }

                this.productModifierGroups = data.GroupList;
            });
    }

    private filterFreeModifierGroups(): void {
        this.freeModifierGroups = [];

        for (const modifier of this.allModifierGroups) {
            if (!this.productModifierGroups.some(x => x.Id === modifier.Id)) {
                this.freeModifierGroups.push(modifier);
            }
        }
    }

    public handleModifierGroupSelected(evt: any): void {
        for (const modifierGroup of this.allModifierGroups) {
            if (modifierGroup.Id === evt.value) {
                this.productModifierGroups.push(modifierGroup);
                break;
            }
        }

        this.productModifierGroupsChanged = true;

        this.filterFreeModifierGroups();
    }

    public handleDeleteModifierGroupClick(modifier: any): void {
        const index = this.productModifierGroups.indexOf(modifier);

        if (index !== -1) {
            this.productModifierGroups.splice(index, 1);
        }

        this.productModifierGroupsChanged = true;

        this.filterFreeModifierGroups();
    }

    private getAllCategories(): void {
        this.foodService.getCategories({} , this.messageService)
            .then(data => {
                if (!data) {
                    return;
                }

                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }

                this.allCategories = data.CategoryList;

                this.filterFreeCategories();
            });
    }

    private getProductCategories(): void {
        this.productCategories = this.product.Categories;
        this.product.Categories = null;
    }

    private filterFreeCategories(): void {
        this.freeCategories = [];

        for (const category of this.allCategories) {
            if (!this.productCategories.some(x => x.Id === category.Id)) {
                this.freeCategories.push(category);
            }
        }
    }

    public handleCategorySelected(evt: any): void {
        for (const category of this.allCategories) {
            if (category.Id === evt.value) {
                this.productCategories.push(category);
                break;
            }
        }

        this.productCategoriesChanged = true;

        this.filterFreeCategories();
    }

    public handleDeleteCategoryClick(category: any): void {
        const index = this.productCategories.indexOf(category);

        if (index !== -1) {
            this.productCategories.splice(index, 1);
        }

        this.productCategoriesChanged = true;

        this.filterFreeCategories();
    }

    public handleUploadClick(): void {
        document.getElementById('idImgInput')?.click();
    }

    public handleImageChange(event: Event) {
        const target = event.target as HTMLInputElement;

        let file: File | null = null;

        if (target.files) {
            file = target.files[0];
        }

        if (!file) {
            return;
        }

        // Chech max file size 2MB
        if (file.size > 2097152) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'This file is bigger than 2MB.' });
            return;
        }

        const reader = new FileReader();

        reader.onload = (e) => {
            if (e?.target?.result) {
                // Base64 string
                this.product.ImageUrl = e.target.result.toString();
            }
        };

        reader.readAsDataURL(file);
    }

    public handleSideBarHide(): void {
        this.onHide();
    }

    public handleTabChange(tab: EditProductTab) {
        this.activeTab = tab;

        if (tab === EditProductTab.ModifierGroups) {
            if (!this.allModifierGroups.length) {
                this.getAllModifierGroups();
            }
        }
        else if (tab === EditProductTab.Categories) {
            if (!this.allCategories.length) {
                this.getAllCategories();
            }
        }
    }

    public handleSaveClick(): void {
        let promise: Promise<any> = Promise.resolve();

        this.product.StatusId = this.isEnabled ? FoodProductStatus.Enabled : FoodProductStatus.Disabled;

        if (this.productModifierGroupsChanged) {
            this.product.ModifierGroupIds = this.productModifierGroups.map(x => x.Id);
        }

        if (this.productCategoriesChanged) {
            this.product.CategoryIds = this.productCategories.map(x => x.Id);
        }

        if (this.productId) {
            promise = this.foodService.updateProduct(this.product, this.messageService);
        } else {
            promise = this.foodService.addProduct(this.product, this.messageService);
        }

        promise.then(data => {
            if (!data) {
                return;
            }

            if (!data.Success) {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                return;
            }

            this.onSave();
        });
    }
}
